import { useState } from 'react';
import type { ProspectDto } from '@signalvote/shared';
import { Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useRemoveProspect } from '@/hooks/queries';

/**
 * Remove one Prospect — a duplicate or someone who shouldn't be on the list — after a confirmation.
 * The server allows it only if nobody has called them; a refusal stays on screen and points to Do Not
 * Call instead. `onRemoved` runs once they're gone — the detail page goes back to the book.
 */
export function ProspectRemove({ prospect, onRemoved }: { prospect: ProspectDto; onRemoved?: () => void }) {
  const [open, setOpen] = useState(false);
  const { mutate: removeProspect, isPending, error, reset } = useRemoveProspect();

  const onOpen = () => {
    reset();
    setOpen(true);
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={onOpen} aria-label={`Remove ${prospect.name}`}>
        <Trash2 className="mr-1.5 h-4 w-4" />
        Remove
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {prospect.name}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  They come off every Prospect List they&apos;re on, with their federal giving matches. Nothing
                  in your finance records changes.
                </p>
                {error && <p className="text-amber-700">{error.message}</p>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Keep them</AlertDialogCancel>
            <AlertDialogAction
              // A refusal won't change on a second try; reopening starts fresh.
              disabled={isPending || !!error}
              onClick={(e) => {
                // Stay open until the removal lands, so a refusal can be shown.
                e.preventDefault();
                removeProspect(
                  { id: prospect.id, concurrencyVersion: prospect.concurrencyVersion },
                  {
                    onSuccess: () => {
                      setOpen(false);
                      onRemoved?.();
                    },
                  },
                );
              }}
            >
              {isPending ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
